/**
 * HubCanvas — the shared "Central Hub", running the REAL game engine in a peaceful hub mode.
 *
 * Your actual ship (loaded from your cloud save, with all your upgrades, turrets, cannons,
 * thrusters and fighters) is piloted with the real movement; other logged-in pilots appear as
 * their real ships. No enemies, no mining, no damage between players — just a social space that
 * co-op dungeons will later launch from. Positions stream over Supabase Realtime (see net/hub.ts),
 * which also shards into "servers" of HUB_CAPACITY pilots each.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { RenderRoot } from "@/render/RenderRoot";
import { GameState } from "@/engine/GameState";
import { GameLoop } from "@/engine/GameLoop";
import { PointerControls } from "@/input/PointerControls";
import { loadCloudSave } from "@/net/cloudSave";
import { joinHub, HUB_CAPACITY, type HubHandle } from "@/net/hub";
import type { Account } from "@/net/auth";
import { t, useLang } from "@/i18n";

interface Props {
  account: Account;
  onExit: () => void;
}

export default function HubCanvas({ account, onExit }: Props) {
  const mountRef = useRef<HTMLDivElement>(null);
  const stateRef = useRef<GameState | null>(null);
  const [count, setCount] = useState(1);
  const [instance, setInstance] = useState(1);
  const [wantInstance, setWantInstance] = useState(0); // 0 = auto-shard; >0 = a chosen server
  const [pilots, setPilots] = useState<string[]>([]);
  useLang();

  // Boot the real engine in hub mode (once).
  useEffect(() => {
    const mount = mountRef.current;
    if (!mount) return;
    let cancelled = false;
    let ready = false;
    const render = new RenderRoot();
    const state = new GameState();
    state.accountName = account.username;
    stateRef.current = state;
    let loop: GameLoop | null = null;
    let controls: PointerControls | null = null;
    render
      .mount(mount)
      .then(async () => {
        if (cancelled) {
          render.destroy();
          return;
        }
        ready = true;
        try {
          const data = await loadCloudSave(account.id); // your real upgrades
          if (cancelled) {
            render.destroy();
            return;
          }
          if (data) state.load(data as Parameters<typeof state.load>[0]);
        } catch (e) {
          console.warn("[hub] save load failed:", e);
        }
        state.startHub(); // peaceful space, real ship
        controls = new PointerControls(mount, render, {
          onTapWorld: (x, y) => state.commandTo(x, y),
          onHover: () => {},
          onHoverEnd: () => {},
        });
        loop = new GameLoop({
          update: (dt) => state.update(dt),
          render: (alpha) => {
            try {
              render.render(state, alpha);
            } catch (e) {
              console.error("[hub] render error:", e);
              loop?.stop();
            }
          },
        });
        loop.start();
      })
      .catch((err) => console.error("[hub] engine init failed:", err));
    return () => {
      cancelled = true;
      loop?.stop();
      controls?.destroy();
      if (ready) render.destroy();
      stateRef.current = null;
    };
  }, [account]);

  // (Re)join the hub channel + broadcast our ship + poll the roster. Re-runs on server switch.
  useEffect(() => {
    const handle: HubHandle = joinHub(
      account,
      {
        onMove: (m) =>
          stateRef.current?.setRemotePlayer(m.id, {
            username: m.username, tier: m.tier, x: m.x, y: m.y, h: m.h, vx: m.vx, vy: m.vy, seg: m.seg,
          }),
        onLeave: (id) => stateRef.current?.removeRemotePlayer(id),
        onCount: setCount,
        onInstance: setInstance,
      },
      wantInstance > 0 ? { instance: wantInstance } : undefined,
    );

    let lastX = NaN;
    let lastY = NaN;
    let lastSend = 0;
    let wasMoving = false;
    const bcast = window.setInterval(() => {
      const st = stateRef.current;
      if (!st) return;
      const m = st.mothership;
      const now = performance.now();
      const moved = isNaN(lastX) || Math.hypot(m.pos.x - lastX, m.pos.y - lastY) > 6;
      const speed = Math.hypot(m.vel.x, m.vel.y);
      const movingNow = speed > 8;
      // Send while moving (~4.5Hz), the instant we STOP (kills dead-reckoning overshoot), or a slow
      // keepalive. Remotes dead-reckon with the sent velocity, so this low rate still reads smooth.
      const stopped = wasMoving && !movingNow;
      if ((movingNow && moved && now - lastSend > 220) || stopped || now - lastSend > 1800) {
        const seg = st.isWorm ? Math.min(st.wormBodyCount, 14) : 0;
        handle.send(
          Math.round(m.pos.x), Math.round(m.pos.y), +m.heading.toFixed(2),
          st.hullTier, Math.round(m.vel.x), Math.round(m.vel.y), seg,
        );
        lastSend = now; lastX = m.pos.x; lastY = m.pos.y;
      }
      wasMoving = movingNow;
    }, 100);

    const poll = window.setInterval(() => {
      const st = stateRef.current;
      if (st) setPilots([...st.remotePlayers.values()].map((p) => p.username));
    }, 700);

    return () => {
      window.clearInterval(bcast);
      window.clearInterval(poll);
      handle.leave();
      stateRef.current?.remotePlayers.clear();
      setPilots([]);
    };
  }, [account, wantInstance]);

  const formation = useCallback(() => stateRef.current?.callFleetFormation(), []);
  const serverDown = useCallback(() => setWantInstance((n) => Math.max(1, (n || instance) - 1)), [instance]);
  const serverUp = useCallback(() => setWantInstance((n) => (n || instance) + 1), [instance]);

  return (
    <div className="hub">
      <div ref={mountRef} className="hub-canvas" />
      <div className="hub-topbar">
        <button className="hub-exit" type="button" onClick={onExit}>
          ← {t("Main menu")}
        </button>
        <div className="hub-server">
          <button className="hub-srv-btn" type="button" onClick={serverDown} aria-label="previous server">◂</button>
          <span className="hub-title">{t("Server")} {instance}</span>
          <button className="hub-srv-btn" type="button" onClick={serverUp} aria-label="next server">▸</button>
        </div>
        <div className="hub-count">
          <span className="hub-count-dot" /> {count}/{HUB_CAPACITY}
        </div>
      </div>
      <div className="hub-testing">🧪 {t("Hub is in testing — things may change or break")}</div>
      <button className="hub-formation" type="button" onClick={formation}>
        ✈ {t("Formation")}
      </button>
      <div className="hub-pilots">
        <div className="hub-pilots-title">{t("Pilots here")}</div>
        <div className="hub-pilot self">{account.username} ({t("you")})</div>
        {pilots.map((n, i) => (
          <div className="hub-pilot" key={`${n}-${i}`}>{n}</div>
        ))}
      </div>
      <div className="hub-hint">{t("Tap to fly — no combat between pilots here")}</div>
    </div>
  );
}
